import React, { useEffect, useMemo, useState } from 'react';
import {
  Briefcase,
  Building2,
  Calendar,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Lock,
  Monitor,
  Search,
  Server,
  ShieldCheck,
  Stethoscope,
  User,
  Users,
} from 'lucide-react';
import { keepPreviousData } from '@tanstack/react-query';

import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { Select } from '../../ComponnentsUI/common/Select';
import { Badge } from '../../ComponnentsUI/common/Badge';
import { Switch } from '../../ComponnentsUI/common/Switch';
import { InlineSpinner } from '../../ComponnentsUI/common/LoadingSpinner';
import { ConfirmDialog } from '../../ComponnentsUI/common/ConfirmDialog';
import {
  ActionDef,
  Role,
  RoleCategory,
  useActionCatalog,
  useRoleMatrix,
  useRoles,
  useSetRoleMatrix,
} from '../../../helpers/api/roles/roles';

const CATEGORY_LABEL: Record<RoleCategory, string> = {
  CLINICAL: 'Clinical',
  ADMINISTRATIVE: 'Administrative',
  EXTERNAL: 'External',
};

const CATEGORY_ICON: Record<RoleCategory, React.ElementType> = {
  CLINICAL: Stethoscope,
  ADMINISTRATIVE: Briefcase,
  EXTERNAL: ExternalLink,
};

const ENTITY_LABEL: Record<string, string> = {
  patient: 'Patients',
  study: 'Studies',
  report: 'Reports',
  user: 'Users',
  org_unit: 'Organization structure',
  role: 'Roles',
  pacs_device: 'PACS devices',
  audit_log: 'Audit log',
  appointment: 'Appointments',
};

// Short blurb under each section title explaining what the entity covers.
const ENTITY_BLURB: Record<string, string> = {
  patient: 'Patient records and demographics',
  study: 'Ultrasound exams and acquisitions',
  report: 'Clinical reports and sign-off',
  user: 'Staff accounts and access',
  org_unit: 'Regions, divisions and facilities',
  role: 'Role definitions and permissions',
  pacs_device: 'Imaging device connectivity',
  audit_log: 'Activity and compliance trail',
  appointment: 'Scheduling and appointments',
};

// Icon + accent color per entity, purely decorative.
const ENTITY_STYLE: Record<string, { icon: React.ElementType; chip: string }> = {
  patient: { icon: User, chip: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  study: { icon: Monitor, chip: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  report: { icon: FileText, chip: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400' },
  user: { icon: Users, chip: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  org_unit: { icon: Building2, chip: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  role: { icon: ShieldCheck, chip: 'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400' },
  pacs_device: { icon: Server, chip: 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300' },
  audit_log: { icon: ClipboardCheck, chip: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  appointment: { icon: Calendar, chip: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
};

const DEFAULT_ENTITY_STYLE = {
  icon: ShieldCheck,
  chip: 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300',
};

// Friendly display names + one-line explanations for raw action keys
// (display only — the raw key is still what gets saved).
const ACTION_META: Record<string, { label: string; hint?: string }> = {
  read: { label: 'View', hint: 'See records and their details' },
  create: { label: 'Create', hint: 'Add new records' },
  write: { label: 'Edit', hint: 'Modify existing records' },
  manage: { label: 'Manage', hint: 'Full control, including deletion' },
  archive: { label: 'Archive', hint: 'Move records out of active use' },
  refer: { label: 'Refer', hint: 'Refer to another clinician or facility' },
  share: { label: 'Share', hint: 'Share with other users' },
  acquire: { label: 'Acquire', hint: 'Start new acquisitions' },
  lock_acquisition: { label: 'Lock acquisition', hint: 'Prevent further changes to an acquisition' },
  write_findings: { label: 'Write findings', hint: 'Enter measurements and findings' },
  amend: { label: 'Amend', hint: 'Correct a finalized report' },
  export_pdf: { label: 'Export PDF', hint: 'Download reports as PDF' },
  sign_routine: { label: 'Sign (routine)', hint: 'Sign off routine exams' },
  sign_specialist: { label: 'Sign (specialist)', hint: 'Sign off specialist exams' },
  sign_mfm: { label: 'Sign (MFM)', hint: 'Sign off maternal-fetal medicine exams' },
  write_report: { label: 'Write report', hint: 'Author report content' },
  assign_roles: { label: 'Assign roles', hint: 'Grant or revoke roles for users' },
  sync: { label: 'Sync', hint: 'Trigger device synchronization' },
  export: { label: 'Export', hint: 'Export data out of the system' },
};

const actionLabel = (key: string) =>
  ACTION_META[key]?.label ?? key.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
const actionHint = (a: ActionDef) => a.description || ACTION_META[a.key]?.hint;

const cellKey = (entity: string, action: string) => `${entity}.${action}`;

const ENTITY_DOMAIN: Record<string, 'CLINICAL' | 'ADMINISTRATIVE'> = {
  patient: 'CLINICAL',
  study: 'CLINICAL',
  report: 'CLINICAL',
  user: 'ADMINISTRATIVE',
  org_unit: 'ADMINISTRATIVE',
  role: 'ADMINISTRATIVE',
  audit_log: 'ADMINISTRATIVE',
};

// Section order for the detail panel: clinical first, then admin, then the rest.
type Domain = 'CLINICAL' | 'ADMINISTRATIVE' | 'GENERAL';
const DOMAIN_ORDER: Domain[] = ['CLINICAL', 'ADMINISTRATIVE', 'GENERAL'];
const DOMAIN_TITLE: Record<Domain, string> = {
  CLINICAL: 'Clinical data',
  ADMINISTRATIVE: 'Administration',
  GENERAL: 'General',
};
const entityDomain = (entity: string): Domain => ENTITY_DOMAIN[entity] ?? 'GENERAL';

// Admin roles that, by exception, are allowed to hold clinical (PHI) permissions —
// a facility admin sees everything at their facility.
const PHI_CAPABLE_ADMIN_ROLES = new Set(['facility_admin']);

const isEntityLocked = (
  entity: string,
  roleCategory: RoleCategory,
  roleKey?: string,
): boolean => {
  const domain = ENTITY_DOMAIN[entity];
  if (!domain) return false;
  if (roleKey && PHI_CAPABLE_ADMIN_ROLES.has(roleKey)) return false;
  return roleCategory === 'ADMINISTRATIVE'
    ? domain === 'CLINICAL'
    : domain === 'ADMINISTRATIVE';
};

function RoleList({
  roles,
  selectedId,
  onSelect,
}: {
  roles: Role[];
  selectedId?: string;
  onSelect: (r: Role) => void;
}) {
  const [roleSearch, setRoleSearch] = useState('');
  const q = roleSearch.trim().toLowerCase();

  const grouped = useMemo(() => {
    const g: Record<RoleCategory, Role[]> = { CLINICAL: [], ADMINISTRATIVE: [], EXTERNAL: [] };
    roles
      .filter(r => !q || r.name.toLowerCase().includes(q))
      .forEach(r => g[r.category]?.push(r));
    return g;
  }, [roles, q]);

  const nothingFound = q && Object.values(grouped).every(list => list.length === 0);

  return (
    <div className="hidden w-64 shrink-0 lg:block">
      <div className="sticky top-4 rounded-2xl border border-gray-200 bg-card-color-100 p-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="relative mb-3">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-title-page-color-300 dark:text-gray-500"
          />
          <Input
            value={roleSearch}
            onChange={e => setRoleSearch(e.target.value)}
            placeholder="Find a role…"
            className="h-9 pl-8"
          />
        </div>
        <div className="space-y-4">
          {nothingFound && (
            <p className="px-2 py-4 text-center text-sm text-title-page-color-300 dark:text-gray-400">
              No role matches “{roleSearch.trim()}”.
            </p>
          )}
          {(Object.keys(grouped) as RoleCategory[]).map(cat => {
            if (!grouped[cat].length) return null;
            const CatIcon = CATEGORY_ICON[cat];
            return (
              <section key={cat}>
                {/* Section */}
                <div className="mb-1.5 flex items-center gap-1.5 rounded-lg bg-card-color-200/80 px-2 py-1.5 dark:bg-gray-700/50">
                  <CatIcon
                    size={13}
                    className="shrink-0 text-title-page-color-300 dark:text-gray-400"
                  />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-title-page-color-300 dark:text-gray-400">
                    {CATEGORY_LABEL[cat]}
                  </h3>
                  <span className="ml-auto rounded-full bg-card-color-100 px-1.5 py-0.5 text-[10px] font-medium dark:bg-gray-800">
                    {grouped[cat].length}
                  </span>
                </div>
                {/* Subsections (roles) */}
                <ul className="ml-2 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-700">
                  {grouped[cat].map(r => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(r)}
                        className={`group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          r.id === selectedId
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-title-page-color-100 hover:bg-card-color-200/60 dark:text-gray-200 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <span className="truncate">{r.name}</span>
                        {r.is_system && (
                          <span
                            className={`ml-auto shrink-0 text-[10px] font-medium uppercase tracking-wide ${
                              r.id === selectedId
                                ? 'text-blue-100'
                                : 'text-title-page-color-300 dark:text-gray-500'
                            }`}
                          >
                            system
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PermissionRow({
  action,
  entity,
  checked,
  changed,
  onToggle,
}: {
  action: ActionDef;
  entity: string;
  checked: boolean;
  changed: boolean;
  onToggle: () => void;
}) {
  const hint = actionHint(action);
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-card-color-200/50 dark:hover:bg-gray-700/40"
      title={cellKey(entity, action.key)}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-title-page-color-100 dark:text-gray-200">
            {actionLabel(action.key)}
          </span>
          {changed && (
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500"
              title="Unsaved change"
            />
          )}
        </div>
        {hint && (
          <div className="truncate text-xs text-title-page-color-300 dark:text-gray-500">
            {hint}
          </div>
        )}
      </div>
      <Switch
        checked={checked}
        onChange={onToggle}
        aria-label={`${actionLabel(action.key)} — ${ENTITY_LABEL[entity] ?? entity}`}
      />
    </div>
  );
}

function EntitySection({
  entity,
  actions,
  allActions,
  allowed,
  changedKeys,
  locked,
  lockReason,
  onToggle,
  onToggleAll,
}: {
  entity: string;
  actions: ActionDef[];
  allActions: ActionDef[];
  allowed: Set<string>;
  changedKeys: Set<string>;
  locked: boolean;
  lockReason: string;
  onToggle: (entity: string, action: string) => void;
  onToggleAll: (entity: string) => void;
}) {
  const style = ENTITY_STYLE[entity] ?? DEFAULT_ENTITY_STYLE;
  const EntityIcon = style.icon;
  const entityKeys = allActions.map(a => cellKey(entity, a.key));
  const onCount = entityKeys.filter(k => allowed.has(k)).length;
  const allOn = onCount === entityKeys.length;
  const entityName = ENTITY_LABEL[entity] ?? entity;

  return (
    <div
      aria-disabled={locked}
      className={`overflow-hidden rounded-2xl border transition-shadow ${
        locked
          ? 'border-gray-200 bg-card-color-200/30 dark:border-gray-700 dark:bg-gray-800/40'
          : 'border-gray-200 bg-white shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 ${
          locked ? 'opacity-70' : 'border-b border-gray-100 dark:border-gray-700/60'
        }`}
      >
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${style.chip}`}>
          <EntityIcon size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-title-page-color-100 dark:text-gray-100">
              {entityName}
            </span>
            {!locked && onCount > 0 && (
              <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {onCount}/{entityKeys.length}
              </span>
            )}
          </div>
          <div className="truncate text-xs text-title-page-color-300 dark:text-gray-500">
            {locked ? lockReason : ENTITY_BLURB[entity] ?? ''}
          </div>
        </div>
        {locked ? (
          <span
            className="flex shrink-0 items-center gap-1 rounded-md bg-card-color-200 px-2 py-1 text-[11px] font-medium text-title-page-color-300 dark:bg-gray-700 dark:text-gray-400"
            title={lockReason}
          >
            <Lock size={11} />
            Locked
          </span>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden text-xs text-title-page-color-300 dark:text-gray-500 sm:inline">
              All
            </span>
            <Switch
              checked={allOn}
              onChange={() => onToggleAll(entity)}
              title={allOn ? `Disable all ${entityName} permissions` : `Enable all ${entityName} permissions`}
              aria-label={`Toggle all ${entityName} permissions`}
            />
          </div>
        )}
      </div>

      {!locked && (
        <div className="grid grid-cols-1 gap-x-4 px-2 py-2 md:grid-cols-2">
          {actions.map(a => {
            const k = cellKey(entity, a.key);
            return (
              <PermissionRow
                key={k}
                action={a}
                entity={entity}
                checked={allowed.has(k)}
                changed={changedKeys.has(k)}
                onToggle={() => onToggle(entity, a.key)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

const RolesPermissionsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { data: roles, isLoading: rolesLoading } = useRoles();
  const { data: actions, isLoading: actionsLoading } = useActionCatalog();
  const [selected, setSelected] = useState<Role | undefined>();
  const [search, setSearch] = useState('');
  // Role the user tried to switch to while having unsaved changes.
  const [pendingRole, setPendingRole] = useState<Role | null>(null);
  const [showDiff, setShowDiff] = useState(false);

  useEffect(() => {
    if (!selected && roles?.length) setSelected(roles[0]);
  }, [roles, selected]);

  const {
    data: matrix,
    isLoading: matrixLoading,
    isFetching: matrixFetching,
    refetch,
  } = useRoleMatrix(selected?.id, { placeholderData: keepPreviousData });
  const [allowed, setAllowed] = useState<Set<string>>(new Set());

  // What the server currently has — used to count/highlight unsaved changes.
  const baseline = useMemo(
    () =>
      new Set(
        (matrix?.cells ?? []).filter(c => c.allowed).map(c => cellKey(c.entity, c.action))
      ),
    [matrix]
  );

  useEffect(() => {
    if (matrix) setAllowed(new Set(baseline));
  }, [matrix, baseline]);

  const changedKeys = useMemo(() => {
    const diff = new Set<string>();
    allowed.forEach(k => {
      if (!baseline.has(k)) diff.add(k);
    });
    baseline.forEach(k => {
      if (!allowed.has(k)) diff.add(k);
    });
    return diff;
  }, [allowed, baseline]);
  const dirty = changedKeys.size > 0;

  const save = useSetRoleMatrix(selected?.id ?? '', () => {
    refetch();
  });

  const byEntity = useMemo(() => {
    const m: Record<string, ActionDef[]> = {};
    (actions ?? []).forEach(a => {
      (m[a.entity] ||= []).push(a);
    });
    return m;
  }, [actions]);

  // Search filters entity sections and their actions. If the entity name itself
  // matches, all of its actions stay visible.
  const query = search.trim().toLowerCase();
  const visibleByEntity = useMemo(() => {
    if (!query) return byEntity;
    const out: Record<string, ActionDef[]> = {};
    Object.keys(byEntity).forEach(entity => {
      const entityName = (ENTITY_LABEL[entity] ?? entity).toLowerCase();
      if (entityName.includes(query) || entity.includes(query)) {
        out[entity] = byEntity[entity];
        return;
      }
      const matching = byEntity[entity].filter(
        a => actionLabel(a.key).toLowerCase().includes(query) || a.key.includes(query)
      );
      if (matching.length) out[entity] = matching;
    });
    return out;
  }, [byEntity, query]);

  // Group visible entities into Clinical / Administration / General sections.
  const sections = useMemo(() => {
    const bySection: Record<Domain, string[]> = {
      CLINICAL: [],
      ADMINISTRATIVE: [],
      GENERAL: [],
    };
    Object.keys(visibleByEntity).forEach(e => bySection[entityDomain(e)].push(e));
    return DOMAIN_ORDER.filter(d => bySection[d].length).map(d => ({
      domain: d,
      entities: bySection[d],
    }));
  }, [visibleByEntity]);

  const toggle = (entity: string, action: string) => {
    const k = cellKey(entity, action);
    setAllowed(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const toggleEntity = (entity: string) => {
    const keys = byEntity[entity].map(a => cellKey(entity, a.key));
    setAllowed(prev => {
      const next = new Set(prev);
      const allOn = keys.every(k => next.has(k));
      keys.forEach(k => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const reset = () => {
    setAllowed(new Set(baseline));
    setShowDiff(false);
  };

  const onSave = () => {
    const cells = (actions ?? []).map(a => ({
      action: a.key,
      entity: a.entity,
      allowed: allowed.has(cellKey(a.entity, a.key)),
    }));
    setShowDiff(false);
    save.mutate({ cells });
  };

  // Guard: don't silently drop unsaved edits when clicking another role.
  const handleSelectRole = (r: Role) => {
    if (dirty && r.id !== selected?.id) {
      setPendingRole(r);
      return;
    }
    setSelected(r);
  };

  // Human-readable list of pending edits for the review panel.
  const pendingChanges = useMemo(
    () =>
      Array.from(changedKeys)
        .map(k => {
          const [entity, ...rest] = k.split('.');
          const action = rest.join('.');
          return { key: k, entity, action, granted: allowed.has(k) };
        })
        .sort((a, b) => a.key.localeCompare(b.key)),
    [changedKeys, allowed]
  );

  // Ctrl/Cmd+S saves when there is something to save.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty && !save.isPending) onSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Warn before closing/reloading the tab with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Header summary: enabled vs. editable (locked entities excluded).
  const summary = useMemo(() => {
    if (!selected) return { on: 0, total: 0 };
    let on = 0;
    let total = 0;
    Object.keys(byEntity).forEach(entity => {
      if (isEntityLocked(entity, selected.category, selected.key)) return;
      byEntity[entity].forEach(a => {
        total += 1;
        if (allowed.has(cellKey(entity, a.key))) on += 1;
      });
    });
    return { on, total };
  }, [byEntity, allowed, selected]);

  if (rolesLoading || actionsLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-card-color-100 py-16 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <InlineSpinner label="Loading roles…" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Standalone route only: inside the Organization tabs the active tab
          already provides this context, so a repeated big title is just noise. */}
      {!embedded && (
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
            <Lock size={24} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
              Roles &amp; Permissions
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Edit what a role <span className="font-medium">means</span> on this instance.
              Changes apply to every user holding the role.
            </p>
          </div>
        </div>
      )}

      {/* On narrower layouts the sidebar is hidden; a simple dropdown picks the role. */}
      <div className="mb-4 lg:hidden">
        <Select
          value={selected?.id ?? ''}
          onChange={e => {
            const r = (roles ?? []).find(x => x.id === e.target.value);
            if (r) handleSelectRole(r);
          }}
        >
          {(['CLINICAL', 'ADMINISTRATIVE', 'EXTERNAL'] as RoleCategory[]).map(cat => {
            const catRoles = (roles ?? []).filter(r => r.category === cat);
            if (!catRoles.length) return null;
            return (
              <optgroup key={cat} label={CATEGORY_LABEL[cat]}>
                {catRoles.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </Select>
      </div>

      <div className="flex gap-6">
        <RoleList roles={roles ?? []} selectedId={selected?.id} onSelect={handleSelectRole} />

        <div className="min-w-0 flex-1">
          {!selected ? (
            <div className="rounded-2xl border border-gray-200 bg-card-color-100 py-10 text-center text-sm text-title-page-color-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              Select a role.
            </div>
          ) : (
            <>
              {/* Role header card */}
              <div className="mb-5 rounded-2xl border border-gray-200 bg-gradient-to-r from-blue-50/80 to-transparent p-5 dark:border-gray-700 dark:from-blue-950/40 dark:to-transparent dark:bg-gray-800">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-md shadow-blue-600/20">
                      <ShieldCheck size={24} />
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-lg font-bold text-title-page-color-100 dark:text-white">
                          {selected.name}
                        </h2>
                        <Badge
                          text={CATEGORY_LABEL[selected.category]}
                          variant={selected.category === 'CLINICAL' ? 'info' : 'neutral'}
                        />
                        {selected.is_system && <Badge text="System role" variant="special" />}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="h-1.5 w-40 overflow-hidden rounded-full bg-white/70 dark:bg-gray-700">
                          <div
                            className="h-full rounded-full bg-blue-500 transition-all duration-300"
                            style={{
                              width: summary.total
                                ? `${Math.round((summary.on / summary.total) * 100)}%`
                                : '0%',
                            }}
                          />
                        </div>
                        <span className="whitespace-nowrap text-xs text-title-page-color-300 dark:text-gray-400">
                          {summary.on} of {summary.total} permissions enabled
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-title-page-color-300 dark:text-gray-400">
                        <span>Assignable at:</span>
                        {selected.assignable_levels?.length ? (
                          selected.assignable_levels.map(lvl => (
                            <span
                              key={lvl}
                              className="rounded-md bg-card-color-200 px-1.5 py-0.5 text-[11px] font-medium capitalize dark:bg-gray-700 dark:text-gray-300"
                            >
                              {lvl.toLowerCase().replace('_', ' ')}
                            </span>
                          ))
                        ) : (
                          <span className="italic">not assignable to an org node</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="relative w-full lg:w-72">
                    <Search
                      size={15}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-title-page-color-300 dark:text-gray-500"
                    />
                    <Input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search permissions…"
                      className="h-9 bg-white pl-9 dark:bg-gray-800"
                    />
                  </div>
                </div>
              </div>

              {matrixLoading ? (
                <div className="space-y-4" aria-busy="true" aria-label="Loading permissions">
                  {[0, 1].map(i => (
                    <div
                      key={i}
                      className="animate-pulse rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-gray-200 dark:bg-gray-700" />
                        <div className="space-y-2">
                          <div className="h-3 w-36 rounded bg-gray-200 dark:bg-gray-700" />
                          <div className="h-2.5 w-52 rounded bg-gray-100 dark:bg-gray-700/60" />
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        {[0, 1, 2, 3].map(j => (
                          <div key={j} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-700/60" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : sections.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-2xl border border-gray-200 bg-card-color-100 py-12 text-center dark:border-gray-700 dark:bg-gray-800">
                  <Search size={28} className="text-title-page-color-300 dark:text-gray-500" />
                  <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                    No permission matches “{search.trim()}”.
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setSearch('')}>
                    Clear search
                  </Button>
                </div>
              ) : (
                <div
                  className={`space-y-7 transition-opacity animate-in fade-in ${
                    matrixFetching ? 'opacity-50' : 'opacity-100'
                  }`}
                >
                  {sections.map(({ domain, entities }) => (
                    <div key={domain}>
                      <div className="mb-3 flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-500">
                          {DOMAIN_TITLE[domain]}
                        </span>
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                      </div>
                      <div className="space-y-4">
                        {entities.map(entity => (
                          <EntitySection
                            key={entity}
                            entity={entity}
                            actions={visibleByEntity[entity]}
                            allActions={byEntity[entity]}
                            allowed={allowed}
                            changedKeys={changedKeys}
                            locked={isEntityLocked(entity, selected.category, selected.key)}
                            lockReason={`Not available for ${CATEGORY_LABEL[
                              selected.category
                            ].toLowerCase()} roles — this domain is out of scope.`}
                            onToggle={toggle}
                            onToggleAll={toggleEntity}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {dirty && (
                <div className="sticky bottom-4 z-10 mt-6 overflow-hidden rounded-2xl border border-blue-200 bg-blue-50/95 shadow-xl backdrop-blur dark:border-blue-900/50 dark:bg-blue-950/90">
                  {showDiff && (
                    <div className="max-h-44 overflow-y-auto border-b border-blue-200/70 px-5 py-3 dark:border-blue-900/50">
                      <ul className="space-y-1">
                        {pendingChanges.map(c => (
                          <li
                            key={c.key}
                            className="flex items-center gap-2 text-xs text-blue-900 dark:text-blue-100"
                          >
                            <span
                              className={`inline-flex w-14 shrink-0 justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                                c.granted
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                              }`}
                            >
                              {c.granted ? 'Grant' : 'Revoke'}
                            </span>
                            <span className="font-medium">
                              {actionLabel(c.action)}
                            </span>
                            <span className="text-blue-700/70 dark:text-blue-300/70">
                              on {ENTITY_LABEL[c.entity] ?? c.entity}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 px-5 py-3.5">
                    <button
                      type="button"
                      onClick={() => setShowDiff(v => !v)}
                      className="text-sm font-medium text-blue-800 underline-offset-2 hover:underline dark:text-blue-200"
                    >
                      {changedKeys.size} unsaved change{changedKeys.size === 1 ? '' : 's'}
                      {showDiff ? ' — hide' : ' — review'}
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="hidden text-xs text-blue-700/70 dark:text-blue-300/60 md:inline">
                        Ctrl+S
                      </span>
                      <Button variant="outline" size="sm" onClick={reset} disabled={save.isPending}>
                        Reset
                      </Button>
                      <Button size="sm" onClick={onSave} disabled={save.isPending}>
                        {save.isPending ? 'Saving…' : 'Save changes'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(pendingRole)}
        title="Discard unsaved changes?"
        message={
          <>
            You have {changedKeys.size} unsaved change{changedKeys.size === 1 ? '' : 's'} on{' '}
            <span className="font-medium">{selected?.name}</span>. Switching to{' '}
            <span className="font-medium">{pendingRole?.name}</span> will discard them.
          </>
        }
        confirmLabel="Discard and switch"
        cancelLabel="Keep editing"
        isPending={false}
        onConfirm={() => {
          if (pendingRole) setSelected(pendingRole);
          setPendingRole(null);
          setShowDiff(false);
        }}
        onCancel={() => setPendingRole(null)}
      />
    </div>
  );
};

export default RolesPermissionsPage;
